// SkuPrefixCollisionsCard — 台帳プレフィックス衝突レポート（読み取り専用）。
//
// 20260610 · 品番ルールが台帳所有のプレフィックス（設備台帳の EQP など）を
// 発番していないかを一覧する。判定材料は全てバックエンド由来
// （GET /api/sku/prefix-collisions · 正本は backend/ledger_counters.py）。
// このカードは「並べるだけ」— 修正操作は置かない。直すかどうかは人が決める。
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Warning, CheckCircle, ArrowsClockwise, CaretDown, CaretRight,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SkuPrefixCollisionsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLedgers, setShowLedgers] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sku/prefix-collisions`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '衝突レポートの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const collisions = data?.collisions || [];
  const ledgers = data?.ledger_prefixes || [];

  return (
    <Card className="p-4" data-testid="sku-prefix-collisions-card">
      <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-bold text-zinc-700 flex items-center gap-1.5">
            {collisions.length > 0
              ? <Warning weight="duotone" size={16} className="text-amber-600" />
              : <CheckCircle weight="duotone" size={16} className="text-teal-700" />}
            台帳プレフィックス衝突レポート
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
            台帳が自前のカウンターで採番しているプレフィックスを、品番ルールが出していないかを照合します。
            一覧のみ・変更は行いません（対応は人が判断してください）。
          </p>
        </div>
        <Button
          size="sm" variant="outline" onClick={load} disabled={loading}
          data-testid="sku-collisions-refresh"
        >
          <ArrowsClockwise size={13} className={loading ? 'animate-spin' : ''} />
          <span className="ml-1">再取得</span>
        </Button>
      </div>

      {data && (
        <div className="text-[11px] text-zinc-500 mb-2" data-testid="sku-collisions-summary">
          品番ルール {data.template_count} 件中 <b className="text-zinc-700">{data.collision_count} 件</b>が台帳プレフィックスと衝突
        </div>
      )}

      {collisions.length === 0 ? (
        <div
          className="rounded-lg border border-teal-200 bg-teal-50/40 px-3 py-4 text-sm text-zinc-600"
          data-testid="sku-collisions-empty"
        >
          衝突している品番ルールはありません。
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="sku-collisions-table">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-200">
                <th className="py-1.5 pr-3 font-semibold">品番ルール</th>
                <th className="py-1.5 pr-3 font-semibold">出すプレフィックス</th>
                <th className="py-1.5 pr-3 font-semibold">所有台帳</th>
                <th className="py-1.5 pr-3 font-semibold">台帳カウンター</th>
                <th className="py-1.5 pr-3 font-semibold">対象アプリ</th>
                <th className="py-1.5 pr-3 font-semibold">発番済</th>
                <th className="py-1.5 pr-3 font-semibold">状態</th>
              </tr>
            </thead>
            <tbody>
              {collisions.map((c) => (
                <tr
                  key={c.template_id}
                  className="border-b border-zinc-100 align-top"
                  data-testid={`sku-collision-row-${c.code}`}
                >
                  <td className="py-1.5 pr-3">
                    <div className="font-semibold text-zinc-800">{c.name}</div>
                    <div className="font-mono text-zinc-500">{c.code}</div>
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-zinc-700">
                    {c.resolved_prefix}
                    {c.sample && <div className="text-zinc-400">例: {c.sample}</div>}
                  </td>
                  <td className="py-1.5 pr-3">
                    <div className="font-semibold text-zinc-700">{c.owner_label}</div>
                    <div className="text-zinc-500">{c.owner_screen}</div>
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-zinc-600">
                    {c.owner_counter}
                    <div className="text-zinc-400">
                      {c.owner_counter_exists ? `現在値 ${c.owner_counter_seq}` : '未使用'}
                    </div>
                  </td>
                  <td className="py-1.5 pr-3 text-zinc-600">
                    {c.app_code || <span className="text-zinc-400">すべてのアプリ</span>}
                  </td>
                  <td className="py-1.5 pr-3 text-zinc-700">{c.issued_count}</td>
                  <td className="py-1.5 pr-3">
                    {c.hard_locked
                      ? (
                        <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-300 text-[10px] px-1.5 py-0">
                          UIで発番不可
                        </Badge>
                      )
                      : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">
                          発番可・要判断
                        </Badge>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowLedgers((v) => !v)}
        className="mt-3 flex items-center gap-1 text-[11px] text-teal-700 hover:underline"
        data-testid="sku-collisions-ledgers-toggle"
      >
        {showLedgers ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
        台帳が所有しているプレフィックス一覧 ({ledgers.length})
      </button>
      {showLedgers && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="sku-collisions-ledger-list">
          {ledgers.map((l) => (
            <div key={l.prefix} className="rounded border border-zinc-200 px-2 py-1.5 flex items-center gap-2">
              <span className="font-mono font-bold text-zinc-700 w-10">{l.prefix}</span>
              <span className="text-[11px] text-zinc-600 flex-1 min-w-0 truncate">{l.owner_screen}</span>
              {l.hard_locked && (
                <span className="text-[10px] text-zinc-500 shrink-0">UIロック</span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
